import { useCallback, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { getClientContext, onClientContextProblem, setClientContext, subscribeClientContext, type ClientContextProblem } from '../../../shared/clientContext';
import { useIdentity } from '../../identity/state/useIdentity';
import { partnerGateway } from '../services';
import { ClientSessionContext, type ClientSessionStatus, type ClientSessionValue } from './ClientSessionContext';
import { partnerErrorMessage } from './partnerError';

/** How long before the 10-minute token ends the portal asks the server for another (a fresh run of every check) while the person is looking at the page. */
const RENEW_LEAD_MS = 60_000;
const CLOCK_TICK_MS = 15_000;

/**
 * Keeps the browser tab honest about which client it is working in. The delegated token is an httpOnly cookie the page cannot read, so everything here comes from `GET /api/me` (the server's own account of the
 * session) and from this tab's own client id in `sessionStorage` (see shared/clientContext.ts): when they disagree because another window opened a different client, this tab says so and does not pretend to work;
 * when the server says the access ended, it says that; when the 10-minute token is about to run out it asks the server to run every check again and issue another, never by refreshing. Nothing here decides access.
 */
export function ClientSessionProvider({ children }: { children: ReactNode }) {
  const { me, refresh } = useIdentity();
  const delegation = me?.partnerDelegation ?? null;
  const tabContext = useSyncExternalStore(subscribeClientContext, getClientContext, () => null);
  const [now, setNow] = useState(() => Date.now());
  const [problem, setProblem] = useState<ClientContextProblem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => onClientContextProblem(setProblem), []);

  // A tab opened fresh while the browser is already inside a client takes up that client; a tab whose session is gone drops its stale label.
  useEffect(() => {
    if (delegation?.isStillValid && tabContext === null) setClientContext(delegation.relationshipId);
    else if (!delegation && tabContext !== null) setClientContext(null);
  }, [delegation, tabContext]);

  const expiresAt = delegation ? Date.parse(delegation.expiresAtUtc) : 0;
  let status: ClientSessionStatus = 'none';
  if (delegation) {
    if (problem === 'mismatch' || (tabContext !== null && tabContext !== delegation.relationshipId)) status = 'otherWindow';
    else if (!delegation.isStillValid || problem === 'invalid') status = 'ended';
    else if (expiresAt <= now) status = 'expired';
    else status = 'active';
  }

  const enter = useCallback(
    async (organizationId: string, relationshipId: string) => {
      const summary = await partnerGateway.enterClient(organizationId, relationshipId);
      setClientContext(relationshipId);
      setProblem(null);
      setNotice(null);
      await refresh();
      return summary;
    },
    [refresh],
  );

  const exit = useCallback(async () => {
    try {
      await partnerGateway.exitClient();
    } catch {
      // Already out (or the access ended): the aim is the same, so clear this tab and ask the server how things stand.
    }

    setClientContext(null);
    setProblem(null);
    setNotice(null);
    await refresh();
  }, [refresh]);

  const renew = useCallback(async () => {
    if (!delegation) return;
    try {
      await partnerGateway.enterClient(delegation.partnerOrganizationId, delegation.relationshipId);
      setClientContext(delegation.relationshipId);
      setProblem(null);
      setNotice(null);
    } catch (error) {
      setNotice(partnerErrorMessage(error, 'Your access to this client could not be renewed.'));
    }

    await refresh();
  }, [delegation, refresh]);

  const adoptCurrentClient = useCallback(() => {
    if (!delegation) return;
    setClientContext(delegation.relationshipId);
    setProblem(null);
    setNotice(null);
  }, [delegation]);

  // Ask for the next token shortly before this one ends, only while the person is looking at the page and this tab is the one that owns the client.
  useEffect(() => {
    if (status !== 'active' || !delegation) return;
    const id = window.setTimeout(() => {
      if (document.visibilityState === 'visible') void renew();
    }, Math.max(0, expiresAt - Date.now() - RENEW_LEAD_MS));
    return () => window.clearTimeout(id);
  }, [status, delegation, expiresAt, renew]);

  const value = useMemo<ClientSessionValue>(
    () => ({
      status,
      delegation,
      notice: notice ?? (problem === 'unavailable' ? 'Your access to this client could not be verified just now. Try again in a moment.' : null),
      enter,
      exit,
      renew,
      adoptCurrentClient,
    }),
    [status, delegation, notice, problem, enter, exit, renew, adoptCurrentClient],
  );

  return <ClientSessionContext.Provider value={value}>{children}</ClientSessionContext.Provider>;
}
